import type { Database } from "@/integrations/supabase/types";

type Card = Database['public']['Tables']['cards']['Row'];

export interface FilterState {
  q: string;
  chips: {
    travel: boolean;
    cashbackBonus: boolean;
    freeAnnual: boolean;
    noForeignFee: boolean;
    visa: boolean;
    mastercard: boolean;
    amex: boolean;
    nfc: boolean;
    prepaid: boolean;
    withAccount: boolean;
    discounts: boolean;
    hotline: boolean;
  };
  advanced: {
    cardTypes: string[];
    interestMax: number | null;
    foreignMax: number | null;
    issuers: string[];
  };
}

type ChipKey = keyof FilterState['chips'];

// Build predicate for base filtering (search + advanced, no chips)
export const makeBasePredicate = (state: FilterState) => (c: Card) => {
  const q = state.q.trim().toLowerCase();
  if (q) {
    const searchable = `${c.display_name || c.name} ${c.issuer}`.toLowerCase();
    if (!searchable.includes(q)) return false;
  }

  const { cardTypes, interestMax, foreignMax, issuers } = state.advanced;
  
  if (cardTypes.length > 0 && (!c.card_type || !cardTypes.includes(c.card_type))) {
    return false;
  }
  
  if (interestMax != null && (c.apr_effective == null || c.apr_effective > interestMax)) {
    return false;
  }
  
  if (foreignMax != null && (c.foreign_tx_fee_percent == null || c.foreign_tx_fee_percent > foreignMax)) {
    return false;
  }
  
  if (issuers.length > 0 && !issuers.includes(c.issuer)) {
    return false;
  }
  
  return true;
};

// Check if card passes a specific chip filter
export const chipPass = (key: ChipKey, c: Card): boolean => {
  const cardAny = c as any;
  switch (key) {
    case 'freeAnnual':
      return (c.annual_fee ?? 0) === 0;
    case 'travel':
      return !!(c.travel_insurance || c.travel_health_insurance || c.travel_discount_text);
    case 'cashbackBonus':
      return !!(c.cashback_program || c.bonus_program);
    case 'noForeignFee':
      return (c.foreign_tx_fee_percent ?? 999) === 0;
    case 'visa':
      return c.network === 'visa';
    case 'mastercard':
      return c.network === 'mastercard';
    case 'amex':
      return c.network === 'american_express';
    case 'nfc':
      return !!c.nfc_contactless;
    case 'prepaid':
      return !!cardAny.is_prepaid;
    case 'withAccount':
      return !!cardAny.current_account_included;
    case 'discounts':
      // Check display_config for hasDiscounts flag
      return !!(cardAny.display_config?.hasDiscounts);
    case 'hotline':
      return !!cardAny.hotline_available;
  }
};

// Compute counts for all chips
export function computeChipCounts(cards: Card[], state: FilterState) {
  const base = makeBasePredicate(state);
  const pool = cards.filter(base);

  const keys: ChipKey[] = [
    'travel',
    'cashbackBonus',
    'freeAnnual',
    'noForeignFee',
    'visa',
    'mastercard',
    'amex',
    'nfc',
    'prepaid',
    'withAccount',
    'discounts',
    'hotline'
  ];
  
  const counts: Record<ChipKey, number> = {} as any;

  for (const k of keys) {
    counts[k] = pool.filter(c => chipPass(k, c)).length;
  }
  
  return { poolCount: pool.length, counts };
}

// Compute card type coverage
export function computeCardTypeCoverage(cards: Card[], state: FilterState) {
  const base = makeBasePredicate(state);
  const pool = cards.filter(base);
  
  return {
    credit: pool.some(c => c.card_type === 'credit'),
    charge: pool.some(c => c.card_type === 'charge'),
    debit: pool.some(c => c.card_type === 'debit'),
  };
}

// Get available issuers from filtered pool
export function getAvailableIssuers(cards: Card[], state: FilterState): string[] {
  const base = makeBasePredicate(state);
  const pool = cards.filter(base);
  return Array.from(new Set(pool.map(c => c.issuer))).sort();
}
